import React from 'react';
import { Divider, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { translate } from '@fiverr-private/i18n-react';
import { Preference } from '../Preference';
import { Gql } from '../../../types';

const T_PREFIX = 'dynamic_matching.freelancer_preferences.fields.type';
const DmBriefAgencyPreference = Gql.DmBriefAgencyPreference;

const getFreelancerTypeText = (value: string) => translate(`${T_PREFIX}.freelancer_type.${value}`);

export const TypePreference = ({
  isVettedPro,
  agencyPreference,
  matchIndicator,
  isSeller,
  sellerName,
  sellerIsVettedPro,
  sellerIsAgency,
}: {
  isVettedPro?: boolean;
  agencyPreference?: Gql.DmBriefAgencyPreference;
  matchIndicator?: Gql.Maybe<Gql.DmBriefMatchIndicator>;
  isSeller: boolean;
  sellerName?: string;
  sellerIsVettedPro?: boolean | null;
  sellerIsAgency?: boolean | null;
}) => {
  const freelancerTypeText: string[] = [];

  if (isSeller) {
    if (isVettedPro) {
      freelancerTypeText.push('prefers_vetted_pro');
    }
    if (agencyPreference === DmBriefAgencyPreference.Prioritize) {
      freelancerTypeText.push('prefers_agencies');
    }
  } else {
    if (isVettedPro) {
      freelancerTypeText.push('priorotize_vetted_pro_freelancers');
    }
    if (agencyPreference === DmBriefAgencyPreference.Prioritize) {
      freelancerTypeText.push('priorotize_agencies');
    } else if (agencyPreference === DmBriefAgencyPreference.Exclude) {
      freelancerTypeText.push('exclude_agencies');
    }
  }

  const tooltipContent = !isSeller ? (
    <TooltipText sellerName={sellerName} isVettedPro={sellerIsVettedPro} isAgency={sellerIsAgency} />
  ) : undefined;

  return agencyPreference !== DmBriefAgencyPreference.Prioritize && !isVettedPro ? null : (
    <Preference name="type" matchIndicator={matchIndicator} tooltipContent={tooltipContent}>
      <Stack width="100%" wrap="wrap" flexShrink={1} direction={{ default: 'column', sm: 'row' }} gap={'1'}>
        {freelancerTypeText.map((value: string, index: number) => (
          <Stack key={index} direction="row" display="inlineFlex" gap="1">
            <Typography color="grey_1200" as="p" size="b_md">
              {getFreelancerTypeText(value)}
            </Typography>
            {index !== freelancerTypeText.length - 1 && (
              <Divider hidden={{ default: true, sm: false }} height="20px" marginX={'2'} direction="vertical" />
            )}
          </Stack>
        ))}
      </Stack>
    </Preference>
  );
};

const TooltipText = ({
  sellerName,
  isVettedPro,
  isAgency,
}: {
  sellerName?: string;
  isVettedPro?: boolean | null;
  isAgency?: boolean | null;
}) => {
  const isCopy = translate(`${T_PREFIX}.tooltip.is`);
  const isntCopy = translate(`${T_PREFIX}.tooltip.isnt`);
  const getIsCopy = (condition?: boolean | null) => (condition ? isCopy : isntCopy);

  const vettedProCopy = translate(`${T_PREFIX}.tooltip.vetted_pro`);
  const agencyCopy = translate(`${T_PREFIX}.tooltip.agency`);

  const vettedProText = `${sellerName} ${getIsCopy(isVettedPro)} ${vettedProCopy}`;

  const agencyText = `${sellerName} ${getIsCopy(isAgency)} ${agencyCopy}`;
  return (
    <>
      {isVettedPro !== undefined && <Typography>{vettedProText}</Typography>}
      {isAgency !== undefined && <Typography>{agencyText}</Typography>}
    </>
  );
};
